import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import AiReviewClient, {
    AiReviewMessage,
    AiReviewProposal,
    AiReviewSessionSummary,
    AiReviewWorkspace,
} from '../apiClients/AiReviewClient';
import { IsEmpty } from '../Helpers';
import { useContextualAiLayers } from '../components/ai/ContextualAiLayerContext';
import useViewportMode, { ViewportMode } from '../hooks/useViewportMode';
import ContextualAiWorkspace from '../components/ai/ContextualAiWorkspace';
import { ContextualAiWorkflowAdapter } from '../components/ai/contextualAiTypes';
import type { AiReviewDataSource } from '../models/AiReviewDataSource';

interface AiReviewPanelProps {
    talentId: string;
    onDraftChanged: () => Promise<void>;
    fillAvailableHeight?: boolean;
    viewportMode?: ViewportMode;
    dataSource?: AiReviewDataSource;
    voiceDisabled?: boolean;
    replayTools?: ReactNode;
}

type RetryOperation = 'conversation' | 'start' | 'chat' | 'proposal' | null;
type RetryChat = { message: string; voiceSessionId: string | null };

const emptyWorkspace = (talentId: string): AiReviewWorkspace => ({
    SessionId: '',
    TalentId: talentId,
    TemplateRevisionId: '',
    IsAiConfigured: true,
    IsFinalized: false,
    ConfigurationMessage: null,
    ContextSummary: '',
    Messages: [],
    Proposals: [],
});

const pick = (source: any, pascalName: string, fallback?: any) => {
    if (source === null || source === undefined)
        return fallback;

    const camelName = pascalName.charAt(0).toLowerCase() + pascalName.slice(1);
    return source[pascalName] ?? source[camelName] ?? fallback;
};

export const normalizeAiReviewWorkspace = (value: unknown, talentId: string): AiReviewWorkspace => {
    const source = value && typeof value === 'object' ? value as any : {};
    const session = pick(source, 'Session', null);
    const context = pick(source, 'Context', null);
    const sessionStatus = `${pick(session, 'Status', '')}`.toLowerCase();
    const messages = Array.isArray(pick(source, 'Messages', []))
        ? pick(source, 'Messages', []).flatMap((message: any) => {
            const role = `${pick(message, 'Role', '')}`.toLowerCase();
            const content = pick(message, 'Content', '');
            if ((role !== 'assistant' && role !== 'user' && role !== 'system-event') || typeof content !== 'string' || IsEmpty(content))
                return [];

            return [{
                Id: pick(message, 'Id'),
                Role: role,
                Content: content,
                CreatedDate: pick(message, 'CreatedDate'),
                Source: pick(message, 'Source', 'Text'),
            } as AiReviewMessage];
        })
        : [];
    const proposals = Array.isArray(pick(source, 'Proposals', []))
        ? pick(source, 'Proposals', []).map((proposal: any) => {
            const type = pick(proposal, 'Type', 'Proficiency');
            const status = pick(proposal, 'Status', 'Pending');
            return ({
                Id: pick(proposal, 'Id', ''),
                Type: type,
                TargetProficiencyId: pick(proposal, 'TargetProficiencyId'),
                TargetName: pick(proposal, 'TargetName', pick(proposal, 'AffectedItemName', 'Review change')),
                CurrentValue: pick(proposal, 'CurrentValue', pick(proposal, 'CurrentRating')),
                ProposedValue: pick(proposal, 'ProposedValue', pick(proposal, 'ProposedRating')),
                CurrentNotes: pick(proposal, 'CurrentNotes'),
                ProposedNotes: pick(proposal, 'ProposedNotes'),
                GoalTitle: pick(proposal, 'GoalTitle'),
                GoalNarrative: pick(proposal, 'GoalNarrative'),
                Rationale: pick(proposal, 'Rationale', ''),
                Evidence: pick(proposal, 'Evidence'),
                Status: status,
                CanApply: Boolean(pick(proposal, 'CanApply', type !== 'TemplateChange')),
                ConflictMessage: pick(proposal, 'ConflictMessage'),
                RowVersion: pick(proposal, 'RowVersion'),
            } as AiReviewProposal);
        })
        : [];

    const directContextSummary = pick(source, 'ContextSummary', '');
    const contextSummary = !IsEmpty(directContextSummary)
        ? directContextSummary
        : context === null
            ? ''
            : [
                !IsEmpty(pick(context, 'TemplateName', '')) ? `${pick(context, 'TemplateName')} template` : '',
                !IsEmpty(pick(context, 'PositionTitle', '')) ? `${pick(context, 'PositionTitle')} position` : '',
                pick(context, 'TemplateProficiencyCount', 0) > 0 ? `${pick(context, 'TemplateProficiencyCount')} required proficiencies` : '',
                !IsEmpty(pick(context, 'LastFinalizedReviewDate', '')) ? 'the last finalized review' : '',
                pick(context, 'UnfinishedGoalCount', 0) > 0 ? `${pick(context, 'UnfinishedGoalCount')} unfinished development goals` : '',
            ].filter(Boolean).join(', ');

    return {
        SessionId: pick(source, 'SessionId', pick(session, 'Id', '')),
        TalentId: pick(source, 'TalentId', pick(session, 'TalentId', talentId)),
        TemplateRevisionId: pick(source, 'TemplateRevisionId', pick(session, 'TemplateRevisionId', '')),
        IsAiConfigured: Boolean(pick(source, 'IsAiConfigured', true)),
        IsFinalized: Boolean(pick(source, 'IsFinalized', sessionStatus === 'finalized' || !IsEmpty(pick(session, 'FinalizedDate', '')))),
        ConfigurationMessage: pick(source, 'ConfigurationMessage'),
        ContextSummary: contextSummary,
        Messages: messages,
        Proposals: proposals,
    };
};

const hasWorkspaceShape = (value: unknown) => {
    if (value === null || typeof value !== 'object')
        return false;

    const source = value as any;
    return source.SessionId !== undefined || source.sessionId !== undefined || source.Session !== undefined || source.session !== undefined ||
        source.Messages !== undefined || source.messages !== undefined;
};

const displayValue = (value: unknown) => {
    if (value === null || value === undefined || value === '')
        return 'Not set';

    return `${value}`;
};

const normalizeSessionSummaries = (value: unknown): AiReviewSessionSummary[] => {
    if (!Array.isArray(value))
        return [];

    return value.flatMap((item: any) => {
        const id = pick(item, 'Id', '');
        const rawStatus = `${pick(item, 'Status', '')}`.toLowerCase();
        const status = rawStatus === 'active' ? 'Active' : rawStatus === 'finalized' ? 'Finalized' : '';
        if (IsEmpty(id) || IsEmpty(status))
            return [];

        return [{
            Id: id,
            TalentId: pick(item, 'TalentId', ''),
            Status: status,
            CreatedDate: pick(item, 'CreatedDate', ''),
            FinalizedDate: pick(item, 'FinalizedDate'),
            FinalizedAssessmentId: pick(item, 'FinalizedAssessmentId'),
            TemplateName: pick(item, 'TemplateName', 'Review'),
        } as AiReviewSessionSummary];
    });
};

const formatSessionDate = (value?: string | null) => {
    if (IsEmpty(value))
        return 'Date unavailable';

    const parsed = new Date(value!);
    return Number.isNaN(parsed.getTime()) ? value! : parsed.toLocaleDateString();
};

const AiReviewPanel = ({
    talentId,
    onDraftChanged,
    fillAvailableHeight = false,
    viewportMode,
    dataSource,
    voiceDisabled = false,
    replayTools,
}: AiReviewPanelProps) => {
    const theme = useTheme();
    const contextualAiLayers = useContextualAiLayers();
    const transcriptMenuRegistered = useRef(false);
    const registerTranscriptMenu = () => {
        if (contextualAiLayers && !transcriptMenuRegistered.current) {
            contextualAiLayers.openTransient();
            transcriptMenuRegistered.current = true;
        }
    };
    const unregisterTranscriptMenu = () => {
        if (contextualAiLayers && transcriptMenuRegistered.current) {
            contextualAiLayers.closeTransient();
            transcriptMenuRegistered.current = false;
        }
    };
    useEffect(() => () => {
        if (contextualAiLayers && transcriptMenuRegistered.current) {
            contextualAiLayers.closeTransient();
            transcriptMenuRegistered.current = false;
        }
    }, [contextualAiLayers]);
    const detectedViewport = useViewportMode();
    // Do not even construct the production client when a replay adapter is
    // injected. The released panel stays unchanged for normal callers while
    // the synthetic capture contract remains transport-free by construction.
    const activeDataSource = useMemo<AiReviewDataSource>(
        () => dataSource ?? new AiReviewClient(),
        [dataSource],
    );
    const activeViewportMode = viewportMode ?? detectedViewport.mode;
    const isTouchLayout = activeViewportMode !== 'desktop';
    const isPhoneLayout = activeViewportMode === 'phone';
    const [workspace, setWorkspace] = useState<AiReviewWorkspace>(() => emptyWorkspace(talentId));
    const [activeWorkspace, setActiveWorkspace] = useState<AiReviewWorkspace>(() => emptyWorkspace(talentId));
    const [sessions, setSessions] = useState<AiReviewSessionSummary[]>([]);
    const [viewedSessionId, setViewedSessionId] = useState<string>('');
    const [historyLoading, setHistoryLoading] = useState<boolean>(false);
    const [historyError, setHistoryError] = useState<string>('');
    const [historyRetrySessionId, setHistoryRetrySessionId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [thinking, setThinking] = useState<boolean>(false);
    const [actionProposalId, setActionProposalId] = useState<string>('');
    const [composerMessage, setComposerMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [retryOperation, setRetryOperation] = useState<RetryOperation>(null);
    const [retryChat, setRetryChat] = useState<RetryChat | null>(null);
    const [retryProposal, setRetryProposal] = useState<{ proposal: AiReviewProposal; action: 'accept' | 'reject' | 'undo' } | null>(null);
    const [conversationExpanded, setConversationExpanded] = useState<boolean>(true);
    const conversationEndRef = useRef<HTMLDivElement>(null);
    const pendingQueueRef = useRef<HTMLDivElement>(null);
    const pendingProposalIdsRef = useRef<Set<string>>(new Set());
    const requestGeneration = useRef<number>(0);
    const activeOperationGeneration = useRef<number>(0);
    const historyRequestGeneration = useRef<number>(0);
    const viewedSessionIdRef = useRef<string>('');
    const activeWorkspaceRef = useRef<AiReviewWorkspace>(activeWorkspace);

    useEffect(() => {
        const generation = ++requestGeneration.current;
        activeOperationGeneration.current += 1;
        viewedSessionIdRef.current = '';
        const resetWorkspace = emptyWorkspace(talentId);
        activeWorkspaceRef.current = resetWorkspace;
        setWorkspace(resetWorkspace);
        setActiveWorkspace(resetWorkspace);
        setViewedSessionId('');
        setComposerMessage('');
        setThinking(false);
        setRetryChat(null);
        setRetryProposal(null);
        setRetryOperation(null);
        setActionProposalId('');
        setConversationExpanded(true);
        pendingProposalIdsRef.current = new Set();

        const initialize = async () => {
            setLoading(true);
            setErrorMessage('');
            try {
                const response = await activeDataSource.GetConversation(talentId);
                if (generation !== requestGeneration.current)
                    return;

                const nextWorkspace = normalizeAiReviewWorkspace(response, talentId);
                if (!nextWorkspace.SessionId && nextWorkspace.IsAiConfigured && !nextWorkspace.IsFinalized) {
                    await startConversation(generation);
                    return;
                }

                activeWorkspaceRef.current = nextWorkspace;
                setActiveWorkspace(nextWorkspace);
                setWorkspace(nextWorkspace);
            } catch (error) {
                if (generation === requestGeneration.current) {
                    setErrorMessage('The review conversation could not be loaded. Your review changes are safe.');
                    setRetryOperation('conversation');
                }
            } finally {
                if (generation === requestGeneration.current)
                    setLoading(false);
            }
        };

        void initialize();
        return () => {
            requestGeneration.current += 1;
            activeOperationGeneration.current += 1;
        };
        // onDraftChanged is intentionally excluded; callers may recreate it after assessment refreshes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDataSource, talentId]);

    useEffect(() => {
        const generation = ++historyRequestGeneration.current;
        let canceled = false;
        setSessions([]);
        setViewedSessionId('');
        setHistoryError('');

        const loadSessions = async () => {
            setHistoryLoading(true);
            try {
                const response = await activeDataSource.GetSessions(talentId);
                if (!canceled && generation === historyRequestGeneration.current)
                    setSessions(normalizeSessionSummaries(response));
            } catch (error) {
                if (!canceled && generation === historyRequestGeneration.current) {
                    setHistoryError('Prior review transcripts could not be loaded.');
                    setHistoryRetrySessionId('');
                }
            } finally {
                if (!canceled && generation === historyRequestGeneration.current)
                    setHistoryLoading(false);
            }
        };

        void loadSessions();
        return () => {
            canceled = true;
            historyRequestGeneration.current += 1;
        };
    }, [activeDataSource, talentId]);

    useEffect(() => {
        if (workspace.Proposals.some((proposal) => proposal.Status === 'Pending'))
            pendingQueueRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        else
            conversationEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }, [workspace.Messages, workspace.Proposals, thinking]);

    useEffect(() => {
        const pendingProposalIds = workspace.Proposals
            .filter((proposal) => proposal.Status === 'Pending')
            .map((proposal) => proposal.Id);
        const hasNewProposal = pendingProposalIds.some((proposalId) => !pendingProposalIdsRef.current.has(proposalId));

        // A new decision deserves the available viewport. Keep the conversation
        // immediately accessible, but do not let it obscure the proposal's
        // identity, evidence, rationale, or actions.
        if (hasNewProposal) {
            setConversationExpanded(false);
            pendingQueueRef.current?.focus({ preventScroll: true });
        }

        pendingProposalIdsRef.current = new Set(pendingProposalIds);
    }, [workspace.Proposals]);

    const startConversation = async (generation = requestGeneration.current) => {
        setThinking(true);
        setErrorMessage('');
        try {
            const response = await activeDataSource.Start(talentId);
            if (generation === requestGeneration.current) {
                const nextWorkspace = normalizeAiReviewWorkspace(response, talentId);
                activeWorkspaceRef.current = nextWorkspace;
                setActiveWorkspace(nextWorkspace);
                setWorkspace(nextWorkspace);
            }
        } catch (error) {
            if (generation === requestGeneration.current) {
                setErrorMessage('Synergist AI could not begin the questionnaire. Your review remains available.');
                setRetryOperation('start');
            }
        } finally {
            if (generation === requestGeneration.current)
                setThinking(false);
        }
    };

    const handleSend = async (messageOverride?: string, voiceSessionId?: string | null): Promise<string | null> => {
        const outgoingMessage = (messageOverride ?? composerMessage).trim();
        const sessionId = activeWorkspace.SessionId;
        if (IsEmpty(outgoingMessage) || IsEmpty(sessionId) || thinking || !IsEmpty(viewedSessionIdRef.current))
            return null;

        const operationGeneration = activeOperationGeneration.current;
        if (messageOverride === undefined)
            setComposerMessage('');
        setThinking(true);
        setErrorMessage('');
        try {
            const response = await activeDataSource.Chat(talentId, {
                SessionId: sessionId,
                Message: outgoingMessage,
                VoiceSessionId: voiceSessionId || null,
            });
            if (operationGeneration !== activeOperationGeneration.current)
                return null;

            const nextWorkspace = normalizeAiReviewWorkspace(response, talentId);
            activeWorkspaceRef.current = nextWorkspace;
            setActiveWorkspace(nextWorkspace);
            if (IsEmpty(viewedSessionIdRef.current))
                setWorkspace(nextWorkspace);
            setRetryOperation(null);
            setRetryChat(null);
            const assistantMessages = nextWorkspace.Messages.filter(message => message.Role === 'assistant');
            return assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1].Content : null;
        } catch (error) {
            if (operationGeneration !== activeOperationGeneration.current)
                return null;
            if (messageOverride === undefined)
                setComposerMessage(outgoingMessage);
            setRetryChat({ message: outgoingMessage, voiceSessionId: voiceSessionId || null });
            setRetryOperation('chat');
            setErrorMessage('Synergist AI could not respond. Your message and review changes have been preserved.');
            return null;
        } finally {
            if (operationGeneration === activeOperationGeneration.current)
                setThinking(false);
        }
    };

    const refreshWorkspace = async (response?: AiReviewWorkspace, operationGeneration = activeOperationGeneration.current) => {
        if (hasWorkspaceShape(response)) {
            const nextWorkspace = normalizeAiReviewWorkspace(response, talentId);
            if (operationGeneration !== activeOperationGeneration.current)
                return;
            activeWorkspaceRef.current = nextWorkspace;
            setActiveWorkspace(nextWorkspace);
            if (IsEmpty(viewedSessionIdRef.current))
                setWorkspace(nextWorkspace);
            return;
        }

        const refreshed = await activeDataSource.GetConversation(talentId);
        if (operationGeneration !== activeOperationGeneration.current)
            return;
        const nextWorkspace = normalizeAiReviewWorkspace(refreshed, talentId);
        activeWorkspaceRef.current = nextWorkspace;
        setActiveWorkspace(nextWorkspace);
        if (IsEmpty(viewedSessionIdRef.current))
            setWorkspace(nextWorkspace);
    };

    const handleViewSession = async (sessionId: string) => {
        const generation = ++historyRequestGeneration.current;
        if (IsEmpty(sessionId)) {
            viewedSessionIdRef.current = '';
            setViewedSessionId('');
            setWorkspace(activeWorkspaceRef.current);
            setHistoryError('');
            setHistoryRetrySessionId('');
            return;
        }

        setHistoryLoading(true);
        viewedSessionIdRef.current = sessionId;
        setViewedSessionId(sessionId);
        setWorkspace({ ...emptyWorkspace(talentId), SessionId: sessionId, IsFinalized: true, IsAiConfigured: activeWorkspace.IsAiConfigured });
        setHistoryError('');
        try {
            const response = await activeDataSource.GetSession(sessionId);
            if (generation !== historyRequestGeneration.current)
                return;

            const historicalWorkspace = normalizeAiReviewWorkspace(response, talentId);
            setWorkspace({ ...historicalWorkspace, IsFinalized: true });
            setHistoryRetrySessionId('');
        } catch (error) {
            if (generation !== historyRequestGeneration.current)
                return;

            setHistoryError('That finalized review transcript could not be opened. The active review remains unchanged.');
            setHistoryRetrySessionId(sessionId);
            viewedSessionIdRef.current = '';
            setViewedSessionId('');
            setWorkspace(activeWorkspaceRef.current);
        } finally {
            if (generation === historyRequestGeneration.current)
                setHistoryLoading(false);
        }
    };

    const handleHistoryRetry = () => {
        if (!IsEmpty(historyRetrySessionId)) {
            void handleViewSession(historyRetrySessionId);
            return;
        }

        const generation = ++historyRequestGeneration.current;
        setHistoryLoading(true);
        setHistoryError('');
        activeDataSource.GetSessions(talentId)
            .then((response) => {
                if (generation === historyRequestGeneration.current)
                    setSessions(normalizeSessionSummaries(response));
            })
            .catch(() => {
                if (generation === historyRequestGeneration.current)
                    setHistoryError('Prior review transcripts still could not be loaded.');
            })
            .finally(() => {
                if (generation === historyRequestGeneration.current)
                    setHistoryLoading(false);
            });
    };

    const handleProposalAction = async (proposal: AiReviewProposal, action: 'accept' | 'reject' | 'undo') => {
        if (!IsEmpty(viewedSessionIdRef.current) || loading)
            return;

        const operationGeneration = activeOperationGeneration.current;
        setActionProposalId(proposal.Id);
        setErrorMessage('');
        const client = activeDataSource;
        let response: AiReviewWorkspace;
        try {
            if (action === 'accept')
                response = await client.AcceptProposal(proposal.Id, proposal.RowVersion);
            else if (action === 'reject')
                response = await client.RejectProposal(proposal.Id, proposal.RowVersion);
            else
                response = await client.UndoProposal(proposal.Id, proposal.RowVersion);

        } catch (error) {
            if (operationGeneration !== activeOperationGeneration.current)
                return;
            const detail = error instanceof Error ? error.message : `${error}`;
            if (detail.toLowerCase().includes('draft changed')) {
                setErrorMessage('The review draft changed after this suggestion was created. Nothing was overwritten; continue the conversation to get a refreshed suggestion.');
                setRetryOperation(null);
                setRetryProposal(null);
            } else {
                setErrorMessage('That proposal could not be updated. No review changes were lost.');
                setRetryOperation('proposal');
                setRetryProposal({ proposal, action });
            }
            if (operationGeneration === activeOperationGeneration.current)
                setActionProposalId('');
            return;
        }

        if (operationGeneration !== activeOperationGeneration.current)
            return;

        setRetryOperation(null);
        setRetryProposal(null);
        try {
            await refreshWorkspace(response, operationGeneration);
        } catch {
            if (operationGeneration === activeOperationGeneration.current)
                setErrorMessage('The proposal was committed, but the AI workspace could not refresh. Reload the review to see the latest draft.');
        }

        if ((action === 'accept' || action === 'undo') && proposal.Type === 'Proficiency') {
            try {
                await onDraftChanged();
            } catch {
                if (operationGeneration === activeOperationGeneration.current)
                    setErrorMessage('The proposal was committed, but the assessment display could not refresh. Reload the review to see the latest draft.');
            }
        }

        if (operationGeneration === activeOperationGeneration.current)
            setActionProposalId('');
    };

    const handleRetry = () => {
        if (retryOperation === 'conversation') {
            requestGeneration.current += 1;
            const generation = requestGeneration.current;
            setLoading(true);
            setErrorMessage('');
            activeDataSource.GetConversation(talentId)
                .then((response) => {
                    if (generation !== requestGeneration.current)
                        return;
                    const next = normalizeAiReviewWorkspace(response, talentId);
                    if (!next.SessionId && next.IsAiConfigured && !next.IsFinalized)
                        return startConversation(generation);
                    activeWorkspaceRef.current = next;
                    setActiveWorkspace(next);
                    setWorkspace(next);
                })
                .catch(() => {
                    if (generation === requestGeneration.current) {
                        setErrorMessage('The review conversation still could not be loaded. Your review changes are safe.');
                        setRetryOperation('conversation');
                    }
                })
                .finally(() => {
                    if (generation === requestGeneration.current)
                        setLoading(false);
                });
        } else if (retryOperation === 'start') {
            void startConversation();
        } else if (retryOperation === 'chat' && retryChat !== null) {
            void handleSend(retryChat.message, retryChat.voiceSessionId);
        } else if (retryOperation === 'proposal' && retryProposal !== null) {
            void handleProposalAction(retryProposal.proposal, retryProposal.action);
        }
    };

    const pendingProposals = workspace.Proposals.filter((proposal) => proposal.Status === 'Pending');
    const resolvedProposals = workspace.Proposals.filter((proposal) => proposal.Status !== 'Pending');
    const finalizedSessions = sessions.filter((session) => session.Status === 'Finalized');

    const renderProposal = (proposal: AiReviewProposal, isNext: boolean = false) => {
        const isTemplateChange = proposal.Type === 'TemplateChange';
        const isActing = actionProposalId === proposal.Id;
        const statusColor = proposal.Status === 'Accepted'
            ? 'success'
            : proposal.Status === 'Rejected'
                ? 'default'
                : proposal.Status === 'Undone'
                    ? 'warning'
                    : 'info';

        const acceptedStatus = proposal.Type === 'DevelopmentGoal'
            ? 'Staged for Publish'
            : proposal.Type === 'TemplateChange'
                ? 'Confirmed suggestion'
                : 'Applied to draft';

        return (
            <Paper
                key={proposal.Id}
                variant="outlined"
                sx={{
                    p: 1.5,
                    borderWidth: isNext ? 2 : 1,
                    borderColor: isNext ? 'primary.main' : isTemplateChange ? 'warning.main' : 'divider',
                    bgcolor: proposal.Status === 'Rejected' ? 'action.disabledBackground' : 'background.paper',
                    opacity: proposal.Status === 'Rejected' ? 0.75 : 1,
                }}
            >
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{proposal.TargetName}</Typography>
                        {isNext && <Chip size="small" label="Next decision" color="primary" />}
                        <Chip size="small" label={proposal.Type === 'DevelopmentGoal' ? 'Development goal' : proposal.Type} variant="outlined" />
                        <Chip size="small" label={proposal.Status === 'Accepted' ? acceptedStatus : proposal.Status} color={statusColor as any} />
                    </Stack>

                    {isTemplateChange && (
                        <Alert severity="warning" sx={{ py: 0 }}>
                            Shared-standard suggestion only. This does not change the employee review or its template and requires separate template administration.
                        </Alert>
                    )}

                    {proposal.Type === 'DevelopmentGoal' ? (
                        <Stack spacing={0.75}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Goal title</Typography>
                                <Typography variant="body2" fontWeight={600}>{proposal.GoalTitle || proposal.TargetName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Development action</Typography>
                                <Typography variant="body2">{proposal.TargetName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Goal narrative</Typography>
                                <Typography variant="body2">{displayValue(proposal.GoalNarrative)}</Typography>
                            </Box>
                            {proposal.Status === 'Accepted' && (
                                <Alert severity="info" sx={{ py: 0 }}>
                                    This goal is staged with the draft. It becomes part of the official review only when you Publish.
                                </Alert>
                            )}
                        </Stack>
                    ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Box sx={{ flex: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Current value</Typography>
                                <Typography variant="body2">{displayValue(proposal.CurrentValue)}</Typography>
                            </Box>
                            <Box sx={{ flex: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Proposed value</Typography>
                                <Typography variant="body2" fontWeight={600}>{displayValue(proposal.ProposedValue)}</Typography>
                            </Box>
                        </Stack>
                    )}

                    {!IsEmpty(proposal.CurrentNotes) && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Current notes</Typography>
                            <Typography variant="body2">{proposal.CurrentNotes}</Typography>
                        </Box>
                    )}
                    {!IsEmpty(proposal.ProposedNotes) && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Proposed notes / evidence</Typography>
                            <Typography variant="body2">{proposal.ProposedNotes}</Typography>
                        </Box>
                    )}
                    <Box>
                        <Typography variant="caption" color="text.secondary">Rationale</Typography>
                        <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {proposal.Rationale}
                        </Typography>
                    </Box>
                    {!IsEmpty(proposal.Evidence) && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Evidence</Typography>
                            <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {proposal.Evidence}
                            </Typography>
                        </Box>
                    )}
                    {!IsEmpty(proposal.ConflictMessage) && <Alert severity="warning">{proposal.ConflictMessage}</Alert>}

                    {!loading && IsEmpty(viewedSessionId) && !workspace.IsFinalized && proposal.Status === 'Pending' && (
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            sx={isTouchLayout ? { '& .MuiButtonBase-root': { minHeight: 44, minWidth: 88 } } : undefined}
                        >
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<CloseRoundedIcon />}
                                disabled={isActing || actionProposalId !== ''}
                                onClick={() => handleProposalAction(proposal, 'reject')}
                            >
                                Reject
                            </Button>
                            {!isTemplateChange && <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircleOutlineIcon />}
                                disabled={!proposal.CanApply || isActing || actionProposalId !== ''}
                                onClick={() => handleProposalAction(proposal, 'accept')}
                            >
                                Accept
                            </Button>}
                        </Stack>
                    )}
                    {!loading && IsEmpty(viewedSessionId) && !workspace.IsFinalized && proposal.Status === 'Accepted' && proposal.CanApply && (
                        <Button
                            size="small"
                            color="inherit"
                            startIcon={<UndoRoundedIcon />}
                            disabled={isActing || actionProposalId !== ''}
                            onClick={() => handleProposalAction(proposal, 'undo')}
                            sx={{ alignSelf: 'flex-end', ...(isTouchLayout && { minHeight: 44, minWidth: 88 }) }}
                        >
                            Undo
                        </Button>
                    )}
                </Stack>
            </Paper>
        );
    };

    const sharedConversationAdapter: ContextualAiWorkflowAdapter<AiReviewWorkspace> = {
        workflow: 'ongoing-assessment',
        sessionKey: `ongoing-assessment:${talentId}:${workspace.SessionId || 'loading'}`,
        voiceWorkflow: 'EmployeeReview',
        contextId: talentId,
        capabilities: { freeText: true, voice: true, editableContextSummary: false },
        initialMessages: workspace.Messages
            .filter(message => message.Role !== 'system-event')
            .map(message => ({ role: message.Role === 'assistant' ? 'assistant' as const : 'user' as const, content: message.Content, source: message.Source === 'Voice' ? 'voice' as const : 'text' as const })),
        initialContextSummary: workspace.ContextSummary,
        progressLabel: workspace.IsFinalized ? 'Finalized review' : 'Review conversation',
        failureMessage: attempt => attempt.source === 'voice'
            ? `Synergist AI could not respond. Your voice turn is ready to retry: “${attempt.text ?? ''}”`
            : 'Synergist AI could not respond. Your message and review changes have been preserved.',
        submit: async request => {
            const assistantMessage = await handleSend(request.text, String(request.metadata?.voiceSessionId ?? '') || null);
            if (!assistantMessage)
                throw new Error('Review assistant did not respond');
            return { assistantMessage, result: activeWorkspaceRef.current };
        },
    };

    return (
        <Paper
            data-testid="ai-review-panel"
            variant="outlined"
            sx={{
                height: fillAvailableHeight ? '100%' : { xs: 620, lg: '100%' },
                minHeight: fillAvailableHeight ? 0 : { xs: 620, lg: 0 },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
            }}
        >
            <Box sx={{ p: isPhoneLayout ? 1.25 : 2, background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})` }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <AutoAwesomeOutlinedIcon color="primary" />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>Synergist AI</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {workspace.IsFinalized ? 'Finalized review transcript' : 'Performance review partner'}
                        </Typography>
                    </Box>
                    <Chip size="small" label={workspace.IsFinalized ? 'Read only' : 'Template & history aware'} variant="outlined" />
                    {historyLoading && <CircularProgress size={18} aria-label="Loading review transcript history" />}
                    {finalizedSessions.length > 0 && (
                        <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{ width: isPhoneLayout ? '100%' : 'auto', minWidth: 0 }}
                        >
                            <HistoryRoundedIcon fontSize="small" color="action" />
                            <Select
                                size="small"
                                value={viewedSessionId}
                                displayEmpty
                                disabled={loading || historyLoading}
                                inputProps={{ 'aria-label': 'Review transcript history' }}
                                onChange={(event) => void handleViewSession(event.target.value as string)}
                                onOpen={registerTranscriptMenu}
                                onClose={unregisterTranscriptMenu}
                                MenuProps={{
                                    MenuListProps: { autoFocusItem: true },
                                    ...(contextualAiLayers ? { className: 'contextual-ai-transient', sx: { zIndex: contextualAiLayers.transientZIndex } } : {}),
                                }}
                                sx={{
                                    minWidth: isPhoneLayout ? 0 : 190,
                                    width: isPhoneLayout ? '100%' : 'auto',
                                    maxWidth: isPhoneLayout ? 'none' : 260,
                                    minHeight: isTouchLayout ? 44 : undefined,
                                    fontSize: '0.8125rem',
                                }}
                            >
                                <MenuItem value="">Review history ({finalizedSessions.length})</MenuItem>
                                {finalizedSessions.map((session) => (
                                    <MenuItem key={session.Id} value={session.Id}>
                                        {session.TemplateName} · {formatSessionDate(session.FinalizedDate || session.CreatedDate)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    )}
                    {!IsEmpty(viewedSessionId) && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArrowBackRoundedIcon />}
                            onClick={() => void handleViewSession('')}
                            sx={isTouchLayout ? { minHeight: 44 } : undefined}
                        >
                            Return to active review
                        </Button>
                    )}
                    {!loading && IsEmpty(viewedSessionId) && workspace.IsAiConfigured && !workspace.IsFinalized && !IsEmpty(workspace.SessionId) && (
                        <Button
                            size="small"
                            variant={conversationExpanded ? 'outlined' : 'contained'}
                            aria-controls="review-ai-conversation"
                            aria-expanded={conversationExpanded}
                            endIcon={(
                                <ExpandMoreRoundedIcon
                                    sx={{ transform: conversationExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
                                />
                            )}
                            onClick={() => setConversationExpanded((expanded) => !expanded)}
                            sx={isTouchLayout ? { minHeight: 44 } : undefined}
                        >
                            {conversationExpanded ? 'Focus suggestions' : 'Open conversation'}
                        </Button>
                    )}
                    {replayTools}
                </Stack>
            </Box>
            <Divider />

            <Stack
                data-testid="ai-review-scroll-region"
                spacing={1.5}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    p: isPhoneLayout ? 1.25 : 2,
                    scrollPaddingBottom: isPhoneLayout ? 88 : undefined,
                }}
            >
                {loading && (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading review conversation...</Typography>
                    </Stack>
                )}

                {!loading && !workspace.IsAiConfigured && (
                    <Alert severity="info">
                        {workspace.ConfigurationMessage || 'Synergist AI is not configured for this environment. You can continue and publish the review manually.'}
                    </Alert>
                )}

                {!IsEmpty(viewedSessionId) && (
                    <Alert severity="info" icon={<HistoryRoundedIcon fontSize="inherit" />}>
                        You are viewing a finalized AI transcript retained as supporting review context. It is read-only and does not replace the official finalized review.
                    </Alert>
                )}

                {!IsEmpty(historyError) && (
                    <Alert
                        severity="warning"
                        action={(
                            <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={handleHistoryRetry}>
                                Retry
                            </Button>
                        )}
                    >
                        {historyError}
                    </Alert>
                )}

                {!IsEmpty(workspace.ContextSummary) && (
                    <Alert severity="info" icon={<AutoAwesomeOutlinedIcon fontSize="inherit" />}>
                        <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>What Synergist AI is using</Typography>
                        <Typography variant="body2">{workspace.ContextSummary}</Typography>
                    </Alert>
                )}

                {pendingProposals.length > 0 && (
                    <Stack
                        ref={pendingQueueRef}
                        spacing={1.25}
                        tabIndex={-1}
                        role="region"
                        aria-label="AI suggestions requiring review"
                        aria-live="polite"
                        data-testid="pending-proposal-queue"
                        sx={{ outline: 'none' }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={700}>Needs your review</Typography>
                                <Typography variant="caption" color="text.secondary">Decide each suggestion individually. Nothing is applied without you.</Typography>
                            </Box>
                            <Chip label={`${pendingProposals.length} pending`} color="primary" />
                        </Stack>
                        {pendingProposals.map((proposal, index) => renderProposal(proposal, index === 0))}
                    </Stack>
                )}

                {workspace.Messages.filter(message => message.Role === 'system-event').map((message, index) => (
                    <Alert
                        key={message.Id || `${message.Role}-${index}`}
                        severity="info"
                        variant="outlined"
                        icon={<HistoryRoundedIcon fontSize="inherit" />}
                        sx={{ alignSelf: 'center', maxWidth: '92%', py: 0 }}
                    >
                        <Typography variant="caption">{message.Content}</Typography>
                    </Alert>
                ))}

                {(workspace.IsFinalized || !IsEmpty(viewedSessionId)) && workspace.Messages
                    .filter(message => message.Role !== 'system-event')
                    .map((message, index) => (
                        <Box
                            key={message.Id || `${message.Role}-${index}`}
                            sx={{ alignSelf: message.Role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%' }}
                        >
                            <Typography variant="caption" color="text.secondary">{message.Role === 'user' ? 'You' : 'Synergist AI'}</Typography>
                            <Paper variant="outlined" sx={{ mt: 0.25, p: 1.15 }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.Content}</Typography>
                            </Paper>
                        </Box>
                    ))}

                {resolvedProposals.length > 0 && (
                    <Accordion variant="outlined" disableGutters data-testid="resolved-proposals">
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2">Resolved suggestions</Typography>
                                <Chip size="small" label={resolvedProposals.length} variant="outlined" />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={1.25}>
                                {resolvedProposals.map((proposal) => renderProposal(proposal))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                )}

                {thinking && (
                    <Alert severity="info" icon={<AutoAwesomeOutlinedIcon fontSize="inherit" />}>
                        Synergist AI is reviewing the template, history, current draft, and your answer...
                    </Alert>
                )}

                {!IsEmpty(errorMessage) && retryOperation !== 'chat' && (
                    <Alert
                        severity="error"
                        action={retryOperation !== null ? (
                            <Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={handleRetry}>Retry</Button>
                        ) : undefined}
                    >
                        {errorMessage}
                    </Alert>
                )}
                {retryOperation === 'chat' && retryChat?.voiceSessionId && (
                    <Alert severity="warning">
                        Voice turn ready to retry: “{retryChat.message}”
                    </Alert>
                )}
                <div ref={conversationEndRef} />
            </Stack>

            {!loading && IsEmpty(viewedSessionId) && workspace.IsAiConfigured && !workspace.IsFinalized && !IsEmpty(workspace.SessionId) && (
                <Box
                    id="review-ai-conversation"
                    aria-hidden={!conversationExpanded}
                    sx={{
                        display: conversationExpanded ? 'block' : 'none',
                        flex: '1 1 45%',
                        minHeight: { xs: 280, sm: 300 },
                        overflow: 'hidden',
                    }}
                >
                    <ContextualAiWorkspace
                        adapter={sharedConversationAdapter}
                        testId="review-ai-workspace"
                        composerTestId="review-ai-composer"
                        sendAriaLabel="Send review message"
                        composerLabel="Message Synergist AI about this review"
                        disabled={thinking}
                        voiceDisabled={voiceDisabled}
                    />
                </Box>
            )}
        </Paper>
    );
};

export default AiReviewPanel;
